import { useEffect, useRef, useState } from "react"
import { Link } from "../lib/router"
import { BRAND } from "../lib/brand"
import { useLanguage } from "../lib/LanguageContext"
import LangToggle from "./LangToggle"

export default function Navbar() {
  const [open, setOpen] = useState(false)
  const [hidden, setHidden] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const lastY = useRef(0)
  const { t } = useLanguage()

  const NAV_LINKS = [
    { href: "#how-it-works", label: t("nav_how_it_works") },
    { href: "#features",     label: t("nav_features") },
    { href: "#clubs",        label: t("nav_clubs") },
    { href: "#faq",          label: t("nav_faq") },
  ]

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY
      setHidden(y > lastY.current && y > 80)
      setScrolled(y > 80)
      lastY.current = y
    }
    window.addEventListener("scroll", onScroll, { passive: true })
    return () => window.removeEventListener("scroll", onScroll)
  }, [])

  useEffect(() => {
    if (!open) return undefined
    const onKeyDown = (e) => {
      if (e.key === "Escape") setOpen(false)
    }
    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [open])

  return (
    <>
      {open && (
        <div id="mobile-menu" className="fixed inset-0 z-menu overflow-y-auto bg-ink-700">
          <div className="flex items-center justify-between px-6 py-4 sm:px-16">
            <a href="#hero" className="flex items-center whitespace-nowrap" onClick={() => setOpen(false)}>
              <img src="/vietnam_aquatics.png" alt={`${BRAND.name} Logo`} className="h-16 w-auto" />
            </a>
            <button
              className="flex h-8 w-8 items-center justify-center border border-white/30 text-paper-0 transition-colors hover:border-white/60"
              aria-label={t("nav_close_menu")}
              onClick={() => setOpen(false)}
            >
              <svg width="11" height="11" viewBox="0 0 14 14" aria-hidden="true">
                <path d="M1 1L13 13M13 1L1 13" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
              </svg>
            </button>
          </div>
          <div className="flex flex-col gap-4 px-6 sm:px-16">
            {NAV_LINKS.map((link) => (
              <a key={link.href} href={link.href} className="font-medium text-paper-0 transition-colors hover:text-gold-400" onClick={() => setOpen(false)}>
                {link.label}
              </a>
            ))}
            <Link to="/results" className="font-medium text-paper-0 transition-colors hover:text-gold-400" onClick={() => setOpen(false)}>
              {t("nav_results")}
            </Link>
          </div>
          <div className="mt-6 flex flex-col gap-3 border-t border-white/[0.16] px-6 pt-6 sm:px-16">
            <LangToggle />
            <Link
              to="/login"
              className="border border-white/30 px-5 py-3 text-center text-sm font-semibold text-paper-0 transition-colors hover:border-white/60"
              onClick={() => setOpen(false)}
            >
              {t("cta_secondary")}
            </Link>
            <Link
              to="/signup"
              className="bg-gold-500 px-5 py-3 text-center text-sm font-semibold text-ink-900 transition-colors hover:bg-gold-400"
              onClick={() => setOpen(false)}
            >
              {t("cta_primary")}
            </Link>
          </div>
        </div>
      )}

      <header
        className={`fixed inset-x-0 top-0 z-header translate-y-0 transition-[transform,background-color] duration-300 ${
          hidden ? "min-[1050px]:-translate-y-full" : ""
        } ${scrolled ? "bg-ink-800/90 backdrop-blur" : "bg-transparent"}`}
      >
        <nav className="flex items-center justify-between px-6 py-3 sm:px-16" aria-label="Primary">
          <a href="#hero" className="flex items-center whitespace-nowrap drop-shadow-sm">
            <img src="/vietnam_aquatics.png" alt={`${BRAND.name} Logo`} className="h-16 w-auto" />
          </a>
          <div className="hidden items-center gap-6 min-[1050px]:flex">
            {NAV_LINKS.map((link) => (
              <a key={link.href} href={link.href} className="font-medium text-paper-0 drop-shadow-sm transition-colors hover:text-gold-400">
                {link.label}
              </a>
            ))}
            <Link to="/results" className="font-medium text-paper-0 drop-shadow-sm transition-colors hover:text-gold-400">
              {t("nav_results")}
            </Link>
          </div>
          <div className="hidden items-center gap-4 min-[1050px]:flex">
            <LangToggle />
            <Link to="/login" className="font-medium text-paper-0 drop-shadow-sm transition-colors hover:text-gold-400">
              {t("cta_secondary")}
            </Link>
            <Link
              to="/signup"
              className="bg-gold-500 px-5 py-3 text-sm font-semibold tracking-[.02em] text-ink-900 transition-colors hover:bg-gold-400"
            >
              {t("cta_primary")}
            </Link>
          </div>
          <button
            className="flex h-8 w-8 items-center justify-center border border-white/30 text-paper-0 transition-colors hover:border-white/60 min-[1050px]:hidden"
            aria-label={open ? t("nav_close_menu") : t("nav_open_menu")}
            aria-expanded={open}
            aria-controls="mobile-menu"
            onClick={() => setOpen((o) => !o)}
          >
            {open ? (
              <svg width="11" height="11" viewBox="0 0 14 14" aria-hidden="true">
                <path d="M1 1L13 13M13 1L1 13" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
              </svg>
            ) : (
              <svg width="14" height="10" viewBox="0 0 18 12" aria-hidden="true">
                <path d="M0 1H18M0 6H18M0 11H18" stroke="currentColor" strokeWidth="1.4" />
              </svg>
            )}
          </button>
        </nav>
      </header>
    </>
  )
}
